import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from '@hub/helpers/use-store';
import { useScript } from '@hub/hooks';
import { EnvService } from '@hub/services/core/env-service';
import { StageOfExit } from '@hub/types/paysafe';
import { PagePath, PaySafeEnvironments } from '@monorepo/client-types';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import { AppLoadingSpinner } from '@monorepo/react-components';
import { useLoading } from '@monorepo/react-hooks';
import {
  CartStep,
  PaymentSessionType,
  SupportedCurrencies,
} from '@monorepo/types';
import { getPayloadForSetup } from './paysafe-checkout.util';

const { application, paysafePublicKey, paysafeEnv } = EnvService.get();

const PaySafeCheckoutPage = () => {
  const paySafeScript = useScript(
    `https://hosted${paysafeEnv === PaySafeEnvironments.Live ? '' : '.test'}.paysafe.com/checkout/v2/paysafe.checkout.min.js`,
  );
  const [paymentSession, setPaymentSession] = useState<PaymentSessionType>();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { setLoadingState, isLoading } = useLoading();
  const paySafeAccounts = useGetFeatureFlag(
    'paysafe-use-currency-accounts-cart',
    {
      [SupportedCurrencies.EUR]: '',
      [SupportedCurrencies.USD]: '',
      [SupportedCurrencies.GBP]: '',
      [SupportedCurrencies.INR]: '',
    },
    {
      env: application,
    },
  );

  const {
    dataStore: { paymentSessionStore },
  } = useStore();

  useEffect(() => {
    const init = async () => {
      if (!sessionId) {
        window.location.href = PagePath.Purchase;
        return;
      }

      try {
        setLoadingState('Loading');
        // Use claim instead of get - this marks the session as opened
        const paymentSessionData =
          await paymentSessionStore.claimPaymentSession(sessionId);
        setPaymentSession(paymentSessionData);
      } catch (error) {
        console.error(error);
        setLoadingState('Loaded');
        // Redirect to purchase page on any error (including 403 - already claimed)
        // Cart is preserved server-side
        let cartUrl = `${PagePath.Purchase}?step=${CartStep.Billing}`;

        // Try to get asset from session for better UX, but don't let it fail the redirect
        try {
          const session =
            await paymentSessionStore.getPaymentSession(sessionId);
          if (session?.purchaseSnapshot?.asset) {
            cartUrl = `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${session.purchaseSnapshot.asset}`;
          }
        } catch (fallbackError) {
          // Silently ignore - we'll redirect to purchase page without asset param
          console.error(
            'Failed to fetch session for fallback redirect:',
            fallbackError,
          );
        }

        // Use window.location.href to force a full page reload and ensure cart data is fresh
        window.location.href = cartUrl;
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (!sessionId || !paymentSession) {
      return;
    }
    if (window.paysafe !== undefined) {
      window.paysafe.checkout.setup(
        paysafePublicKey,
        getPayloadForSetup(paymentSession, paySafeAccounts),
        async function (instance, error, result) {
          try {
            if (!result || !result.paymentHandleToken || error) {
              throw new Error(error.message);
            }

            const { redirectUrl } =
              await paymentSessionStore.confirmPaymentHandleToken(
                result.paymentHandleToken,
                sessionId,
              );

            window.location.href = redirectUrl;
          } catch (error) {
            console.error(error);
            return;
          }
        },
        function (stage: StageOfExit, expired) {
          if (expired) {
            setTimeout(() => {
              window.location.href = `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${paymentSession.purchaseSnapshot.asset}`;
            }, 100);
          }

          const routes: Record<StageOfExit, string> = {
            [StageOfExit.NOT_STARTED]: `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${paymentSession.purchaseSnapshot.asset}`,
            [StageOfExit.IN_REDIRECT]: `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${paymentSession.purchaseSnapshot.asset}`,
            [StageOfExit.AFTER_REDIRECT]: `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${paymentSession.purchaseSnapshot.asset}`,
          };

          const path =
            routes[stage] ||
            `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${paymentSession.purchaseSnapshot.asset}`;

          setTimeout(() => {
            window.location.href = path;
          }, 100);
        },
      );
    }
  }, [paymentSession, paySafeScript]);

  return isLoading ? <AppLoadingSpinner application={application} /> : <></>;
};

export default PaySafeCheckoutPage;
